import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, HelpCircle } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";
import { useStructuredData } from "../hooks/useStructuredData";
import { PAGE_SEO } from "../lib/seo";

// Placeholder Q&A — Scott will provide the real copy.
// Once content is confirmed, just edit the strings below.
// `aText` mirrors `a` as a plain string so the FAQ schema feeds Google
// clean text even when the rendered answer is JSX with inline links.
const FAQS = [
  {
    q: "Do you offer gluten free?",
    a: "All of our Ice Cream bases are gluten free, meaning the only thing that may change this is your option choices if you build it yourself! Please let our team know if you're wanting gluten free and they will be happy to assist!",
    aText: "All of our Ice Cream bases are gluten free, meaning the only thing that may change this is your option choices if you build it yourself. Please let our team know if you're wanting gluten free and they will be happy to assist.",
  },
  {
    q: "Do you do dairy free?",
    a: "Yes — all of our Rolled Ice Creams are available in a dairy free option for an extra $2.50.",
    aText: "Yes — all of our Rolled Ice Creams are available in a dairy free option for an extra $2.50.",
  },
  {
    q: "Can you roll your ice cream yourself?",
    a: "Yes you can! We charge an extra $2 and our team will help you make your own ice cream from start to finish (with our help of course!)",
    aText: "Yes you can. We charge an extra $2 and our team will help you make your own ice cream from start to finish (with our help of course).",
  },
  {
    q: "Do you offer inside seating?",
    a: "Yes we do! Due to the store size it is limited, however we do have some in-store. We also have outside seating which is awesome on a sunny day — plus games for the kids to play!",
    aText: "Yes we do. Due to the store size it is limited, however we do have some in-store. We also have outside seating which is awesome on a sunny day, plus games for the kids to play.",
  },
  {
    q: "Do you do private bookings?",
    a: (
      <>
        Yes we do! Come to us or we&apos;ll come to you. Just enquire via our{" "}
        <Link to="/events" className="text-[#00b4ff] hover:text-[#ff1a8c] underline underline-offset-4 decoration-[#00b4ff]/40 transition-colors">
          Events tab
        </Link>{" "}
        to get in touch.
      </>
    ),
    aText: "Yes we do. Come to us or we'll come to you. Just enquire via our Events tab at https://justdessertme.co.nz/events to get in touch.",
  },
  {
    q: "Do you offer student discounts?",
    a: "Yes we do! Get 10% off with a valid Student ID in store. We also have 20% off for SuperGold card holders!",
    aText: "Yes we do. Get 10% off with a valid Student ID in store. We also have 20% off for SuperGold card holders.",
  },
  {
    q: "How fresh are our waffles and pancakes?",
    a: "Unlike a lot of our competitors, our waffles in Hamilton are made FRESH at time of purchase (not reheated!) and our pancakes are exactly the same. We take pride in serving the freshest waffles and pancakes Hamilton can offer!",
    aText: "Unlike a lot of our competitors, our waffles in Hamilton are made fresh at the time of purchase (not reheated) and our pancakes are exactly the same. We take pride in serving the freshest waffles and pancakes Hamilton can offer.",
  },
  {
    q: "Do you offer sponsorship or fundraising?",
    a: (
      <>
        Yes we do! Reach out to us via our{" "}
        <Link to="/contact" className="text-[#00b4ff] hover:text-[#ff1a8c] underline underline-offset-4 decoration-[#00b4ff]/40 transition-colors">
          contact page
        </Link>{" "}
        and we&apos;d be happy to talk further. Community is important to us!
      </>
    ),
    aText: "Yes we do. Reach out to us via our contact page at https://justdessertme.co.nz/contact and we'd be happy to talk further. Community is important to us.",
  },
];

const FAQ = () => {
  usePageMeta(PAGE_SEO.faq);
  useStructuredData("faq-page", {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.aText,
      },
    })),
  });
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <main data-testid="page-faq" className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
      {/* HERO */}
      <section className="text-center fade-up mb-14">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#00b4ff]/60 text-[0.7rem] uppercase tracking-[0.28em] text-[#00b4ff] mb-5">
          <HelpCircle className="w-3.5 h-3.5" />
          Got questions?
        </span>
        <h1 className="font-script text-6xl sm:text-7xl lg:text-[7rem] neon-gradient-text leading-[0.95]">
          Sweet<br />Answers.
        </h1>
        <p className="text-gray-300 mt-6 max-w-xl mx-auto leading-relaxed">
          Everything you need to know about ordering, shipping, custom orders and what makes our desserts a little different.
        </p>
      </section>

      {/* FAQ ACCORDION */}
      <section className="flex flex-col gap-4" data-testid="faq-list">
        {FAQS.map((item, i) => {
          const isOpen = openIndex === i;
          return (
            <article
              key={item.q}
              data-testid={`faq-item-${i}`}
              className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
                isOpen
                  ? "border-[#ff1a8c]/60 bg-[#ff1a8c]/5 shadow-[0_0_36px_rgba(255,26,140,0.18)]"
                  : "border-white/10 bg-black/30 hover:border-[#00b4ff]/40"
              }`}
            >
              <button
                data-testid={`faq-toggle-${i}`}
                onClick={() => setOpenIndex(isOpen ? -1 : i)}
                className="w-full flex items-center justify-between gap-5 px-6 py-5 text-left"
                aria-expanded={isOpen}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#00b4ff] shrink-0">
                    Q.0{i + 1}
                  </span>
                  <h2 className="text-base sm:text-lg font-semibold text-white truncate-2">
                    {item.q}
                  </h2>
                </div>
                <span
                  className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center transition-all ${
                    isOpen
                      ? "border-[#ff1a8c] bg-[#ff1a8c] text-black"
                      : "border-white/20 text-white"
                  }`}
                  aria-hidden="true"
                >
                  {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                </span>
              </button>
              <div
                data-testid={`faq-answer-${i}`}
                className={`grid transition-all duration-300 ease-out ${
                  isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                }`}
              >
                <div className="overflow-hidden">
                  <p className="px-6 pb-6 pt-1 text-gray-300 leading-relaxed text-sm sm:text-base">
                    {item.a}
                  </p>
                </div>
              </div>
            </article>
          );
        })}
      </section>

      {/* CTA */}
      <section className="text-center mt-16 fade-up">
        <p className="text-sm text-gray-400 mb-3 uppercase tracking-[0.22em]">
          Can&apos;t find your answer?
        </p>
        <a
          href="/contact"
          data-testid="faq-contact-cta"
          className="neon-btn inline-flex"
        >
          Get in touch
        </a>
      </section>
    </main>
  );
};

export default FAQ;
